import type { AuctisyncSupabase } from './client.js';

/**
 * Reading a bidder's own invoices (Run Brief P2.7).
 *
 * WHY THIS LIVES HERE. The website reads invoices in three places
 * (`BidderInvoices`, `BidderInvoiceDetail`, `BidderDashboard`) and labels the
 * status in two of them. This is a bill — the wording a bidder reads about what
 * they owe has to be the SAME sentence on the phone and on the site, or the two
 * surfaces disagree about their money. Jim's rule: no mechanical divergence
 * between the app and the website, wording included.
 *
 * READ ONLY, DELIBERATELY. Nothing here creates a PaymentIntent, confirms a
 * payment or mutates an invoice. P2.7's acceptance is "render via api-client";
 * taking a card on the phone means the Stripe React Native SDK, a Connect-scoped
 * account and a 3DS flow, and that is a money-path chunk of its own rather than
 * a rider on a rendering one.
 *
 * RLS does the scoping: `invoices_self_select` is `user_id = auth.uid()` and
 * `invoice_line_items_via_invoice_self` inherits it. A signed-out caller reads
 * nothing. `user_id` is still passed explicitly below so the intent is legible
 * at the call site and the query is indexed (`idx_invoices_user`).
 */

/** The invoice fields a bidder's own surfaces need. */
export interface MyInvoice {
  id: string;
  invoice_number: string | null;
  status: string;
  subtotal_cents: number;
  buyers_premium_cents: number;
  online_premium_cents: number;
  sales_tax_cents: number;
  shipping_cents: number;
  total_cents: number;
  currency: string;
  created_at: string;
  issued_at: string | null;
  due_at: string | null;
  paid_at: string | null;
  pickup_summary: string | null;
  notes: string | null;
  auction_id: string;
  /** Null when the auction row is not readable, not when it does not exist. */
  auction: { id: string; title: string; slug: string } | null;
}

/** One sold lot on an invoice. Every figure is a snapshot taken at issue time. */
export interface MyInvoiceLine {
  id: string;
  description: string;
  lot_number: number;
  hammer_cents: number;
  buyers_premium_cents: number;
  sales_tax_cents: number;
  line_total_cents: number;
  sort_order: number;
}

/**
 * NOTE: like `PUBLIC_LOT_COLUMNS`, these MUST stay single string literals.
 * supabase-js infers the row shape from the LITERAL type passed to `.select()`,
 * and a runtime-computed string collapses that inference to `GenericStringError`
 * — every field access downstream then fails to typecheck.
 */
const INVOICE_COLUMNS =
  'id, invoice_number, status, subtotal_cents, buyers_premium_cents, online_premium_cents, sales_tax_cents, shipping_cents, total_cents, currency, created_at, issued_at, due_at, paid_at, pickup_summary, notes, auction_id, auctions!invoices_auction_id_fkey(id, title, slug)';

const LINE_COLUMNS =
  'id, description, lot_number, hammer_cents, buyers_premium_cents, sales_tax_cents, line_total_cents, sort_order';

/**
 * The foreign key is named explicitly (`auctions!invoices_auction_id_fkey`)
 * rather than left to PostgREST's inference, matching the website's query.
 * Where two foreign keys reach the same table, an unqualified embed is
 * ambiguous and fails at runtime rather than at compile time.
 */
function toInvoice(row: Record<string, unknown>): MyInvoice {
  const { auctions, ...rest } = row as Record<string, unknown> & {
    auctions?: { id: string; title: string; slug: string } | null;
  };
  return { ...(rest as unknown as Omit<MyInvoice, 'auction'>), auction: auctions ?? null };
}

/**
 * A bidder's invoices across every house they buy from, newest first.
 *
 * Not scoped to one tenant on purpose. One AuctiSync account can be registered
 * with several houses, and "what do I owe" is a question about all of them at
 * once — filtering to the currently selected house would hide a bill.
 */
export async function fetchMyInvoices(
  supabase: AuctisyncSupabase,
  userId: string,
): Promise<MyInvoice[]> {
  const { data, error } = await supabase
    .from('invoices')
    .select(INVOICE_COLUMNS)
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []).map((row) => toInvoice(row as unknown as Record<string, unknown>));
}

/** One invoice with its lines, or null when it is not the caller's to read. */
export interface MyInvoiceDetail {
  invoice: MyInvoice;
  lines: MyInvoiceLine[];
}

export async function fetchMyInvoice(
  supabase: AuctisyncSupabase,
  invoiceId: string,
): Promise<MyInvoiceDetail | null> {
  // Sequenced rather than parallel: with no invoice there is nothing to read
  // lines for, and RLS already refuses the lines of somebody else's invoice.
  const { data, error } = await supabase
    .from('invoices')
    .select(INVOICE_COLUMNS)
    .eq('id', invoiceId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) return null;

  const { data: lineRows, error: lineError } = await supabase
    .from('invoice_line_items')
    .select(LINE_COLUMNS)
    .eq('invoice_id', invoiceId)
    .order('sort_order', { ascending: true });
  if (lineError) throw new Error(lineError.message);

  return {
    invoice: toInvoice(data as unknown as Record<string, unknown>),
    lines: (lineRows ?? []) as unknown as MyInvoiceLine[],
  };
}

/**
 * What a bidder is told their invoice's state is.
 *
 * The website renders the raw column value for `void` and `refunded` — a bidder
 * reads the literal word "void" in a badge, which is database vocabulary, not
 * English anyone is owed. Every status gets a real label here, and both
 * surfaces call this one function.
 *
 * The unknown-status fallback returns the raw value rather than something
 * reassuring: a status this code has not been taught is a state nobody should
 * be told is fine.
 */
export function invoiceStatusLabel(status: string): string {
  switch (status) {
    case 'draft':
      return 'Not yet issued';
    case 'issued':
      return 'Due';
    case 'partially_paid':
      return 'Partially paid';
    case 'paid':
      return 'Paid';
    case 'refunded':
      return 'Refunded';
    case 'void':
      return 'Cancelled';
    default:
      return status;
  }
}

/** How a status should READ — the shared vocabulary behind each surface's own badge styling. */
export type InvoiceTone = 'due' | 'paid' | 'neutral';

export function invoiceStatusTone(status: string): InvoiceTone {
  if (status === 'paid') return 'paid';
  if (status === 'issued' || status === 'partially_paid') return 'due';
  return 'neutral';
}

/**
 * The tag COLOUR each tone is drawn in — one rule, both surfaces.
 *
 * 🔴 THIS LIVES HERE BECAUSE THE TWO SURFACES HAD ALREADY DRIFTED. The website
 * mapped these inline (`paid ? 't-nr' : due ? 't-live' : 't-n'`); the phone
 * reached for its own set the day it grew a shared `Tag`, and picked green for
 * `due`. Green on money a bidder still OWES reads as "nothing to do here",
 * which is the opposite of true — and it was a second implementation of a
 * rule, which is the thing `feedback_no_mechanical_divergence` forbids whether
 * or not the two agree on the day they are written.
 *
 * ⚠️ `due` IS THE LIVE AMBER, NOT A GREEN AND NOT THE BRAND. Amber is the one
 * locked colour that means "this needs you now", which is exactly what an
 * unpaid bill is. The phone's first version used the house's PRIMARY at 10%,
 * which is tenant-restylable — a house with a pale brand got an invisible chip
 * on a statement about somebody's money.
 *
 * ⚠️ `paid` IS THE NO-RESERVE GREEN, NOT THE WINNING GREEN. They are different
 * facts and the website already distinguished them: winning is a live standing
 * that can still be lost, and a settled invoice is neither.
 */
export type InvoiceTagTone = 'noReserve' | 'live' | 'neutral';

export function invoiceStatusTagTone(status: string): InvoiceTagTone {
  const tone = invoiceStatusTone(status);
  if (tone === 'paid') return 'noReserve';
  if (tone === 'due') return 'live';
  return 'neutral';
}

/**
 * Whether money is still owed on this invoice.
 *
 * ⚠️ THERE IS DELIBERATELY NO `amountDueCents()` HERE. `invoices` carries no
 * balance column and `payments` is the source of truth for what has actually
 * been taken, so on a `partially_paid` invoice the only honest answer to "how
 * much is left" is one this module cannot compute. The website hit the same
 * wall and excluded `partially_paid` from card payment for exactly this reason
 * (money-safety finding #3 — charging `total_cents` against a part-paid invoice
 * overcharges). Rendering a subtracted-from-nothing figure next to the word
 * "due" would be a made-up number about somebody's money.
 */
export function invoiceIsOutstanding(status: string): boolean {
  return status === 'issued' || status === 'partially_paid';
}

/**
 * Whether the total shown is the full amount still payable.
 *
 * False for `partially_paid`: some of that total is already paid and the
 * remainder is not known here. Callers use this to decide whether the total may
 * be presented as an amount owed, or only as the invoice's face value.
 */
export function totalIsAmountOwed(status: string): boolean {
  return status === 'issued';
}
